/* eslint-disable unicorn/no-array-callback-reference */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchFilterSizeMobile } from 'search/components/searchGrid/SearchFilterSizeMobile';
import styled from 'styled-components';
import { t } from 'localization/utils/t';
import { variableFontRegular } from 'styles/theme';
import { SearchFilterPriceBucketMobile } from 'search/components/searchGrid/SearchFilterPriceBucketMobile';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { SearchFilterBrandCrawler } from 'search/components/searchGrid/SearchFilterBrandCrawler';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { SearchFilterCategoryMobileCrawler } from 'search/components/searchGrid/SearchFilterCategoryMobileCrawler';
import { useIsCrawler } from 'shared/hooks/useIsCrawler';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { FUTURE, PRESENT } from 'timeline/enums/constants';
import { NAME_DISPLAY, QUERY_NAME_DISPLAY, SORT_FILTER_MAP, TopTitleFilter } from './constant';
import {
  getRangesFromYears,
  getTimeStamps,
  isSortablelist,
  PRICE_BUCKETS_FACET,
  transformConsumerSearchSizeData,
} from './utils/gridUtils';
import DropdownBody from '../dropdowns/DropdownBody';
import { DropdownTrigger } from '../dropdowns/DropdownTrigger';

const TP = 'search.components.searchGrid.SearchSortAndFiltersModal';

const TRIGGER_NAME = {
  sortType: 'Sort',
  brandsList: 'Brand',
  productCategoriesList: 'Category',
  gendersList: 'Gender',
  sizesList: 'Size',
  priceList: 'Price',
  releaseYearsList: 'Year',
  productConditionsList: 'Condition',
  colorsList: 'Color',
};
const DropdownBodyDrops = ({
  topTitle,
  query,
  openDropdown,
  setOpenDropdown,
  rounded,
}: {
  topTitle: string;
  query: any;
  openDropdown: any;
  setOpenDropdown: any;
  rounded?: boolean;
}) => {
  const queryName = QUERY_NAME_DISPLAY[topTitle as TopTitleFilter] || topTitle;
  const eventTrackingEventContext = useEventContext();

  if (!TRIGGER_NAME[topTitle]) {
    return null;
  }

  // get query name eg: brands for brandsList
  const isYearsList = queryName === 'releaseYears';
  const isPriceList = queryName === 'price' || queryName === 'priceList';
  const isSizeList = topTitle === 'sizesList';

  let isQuerySelected = isYearsList
    ? (query?.[queryName] || query.releaseDateStart || query.releaseDateEnd || '').length > 0
    : (query?.[queryName] || '').length > 0;

  if (queryName === 'categories') {
    isQuerySelected =
      (query?.[queryName] || '').length > 0 ||
      (query?.types || '').length > 0 ||
      (query?.activities || '').length > 0;
  } else if (isPriceList) {
    isQuerySelected =
      (query?.priceMin || '').length > 0 ||
      (query?.priceMax || '').length > 0 ||
      Boolean(query?.prices && (query?.prices as string).length > 0) ||
      (query?.price || '').length > 0;
  } else if (isSizeList) {
    isQuerySelected = (query?.sizes || '').length > 0;
  }

  return (
    <DropdownTrigger
      isSelected={
        ((query?.[queryName] || '').length === 0 && openDropdown === topTitle) || isQuerySelected
      }
      title={isYearsList ? 'Year' : TRIGGER_NAME[topTitle]}
      onClick={() => {
        setOpenDropdown((prev: any) => (prev === topTitle ? undefined : topTitle));

        sendTrackingEvent('FILTER_EXPAND_TAP', {
          service_name: 'consumer_search',
          event_context: eventTrackingEventContext,
          filter_name: TRIGGER_NAME[topTitle],
          ...(query?.query && { search_string: query.query.toString() }),
        });
      }}
      rounded={rounded}
    />
  );
};

// eslint-disable-next-line no-empty-pattern
export const MobileFilter = ({
  filters,
  isCrawler,
  timelineData = { timelineType: null, isTimelinePath: false },
}) => {
  const router = useRouter();
  const { query } = router || {};
  const { currentTheme } = useStoreSelector((state) => state.pageLayout);
  const hasTouchedFilters = useRef<any>(false);

  const { doNotCrawlPaths } = useIsCrawler();
  const forCrawler = isCrawler && !doNotCrawlPaths;
  const [chartSize, setChartSize] = useState<any>();
  const [selectedFilterValues, setSelectedFilterValues] = useState([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState<any>([]);
  const eventTrackingEventContext = useEventContext();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    setSelectedFilterValues((query?.sizes as string)?.split(',') || []);
    setSelectedPriceRange((query?.prices as string)?.split(',') || []);
  }, [query?.query, query?.sizes, query?.prices]);

  // Additional effect to handle reset when price-related params are cleared
  useEffect(() => {
    // Check if price-related parameters are not in the URL
    const hasPriceParams = query?.prices || query?.priceMin || query?.priceMax;

    if (!hasPriceParams) {
      // Reset price range state when price params are not present
      setSelectedPriceRange([]);
    }
  }, [query?.prices, query?.priceMin, query?.priceMax]);

  useEffect(() => {
    const getSizeFilters = async () => {
      const availableFilters = await transformConsumerSearchSizeData(filters.sizesList);

      setChartSize(availableFilters);
    };

    if (filters && filters.sizesList?.length > 0) getSizeFilters();
  }, [filters]);

  const [openDropdown, setOpenDropdown] = useState<any>();

  // Add click-outside functionality to close dropdown modals
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
      if (openDropdown) {
        const target = event.target as Element;
        // Check if click is outside dropdown-body and dropdown triggers
        if (
          !target.closest('.dropdown-body') &&
          !target.closest('[data-qa*="mobile_sort_filter_wrapper"]')
        ) {
          // Prevent the click event from propagating to other elements
          event.preventDefault();
          event.stopPropagation();
          setOpenDropdown(undefined);
        }
      }
    };

    if (openDropdown) {
      document.addEventListener('click', handleDocumentClick, true); // Use capture phase
      document.addEventListener('touchend', handleDocumentClick, true); // Use capture phase
    }

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('touchend', handleDocumentClick, true);
    };
  }, [openDropdown]);

  const getSortedFilterList = useCallback((arr: any[] = [], title) => {
    if (!isSortablelist(title) || !arr || arr.length <= 1) return arr;
    return arr.sort((a, b) => {
      if (title === 'releaseYearsList' || title === 'priceList') {
        return `${b.name || b}`.localeCompare(a.name || a, undefined, {
          numeric: true,
        });
      }
      return `${a.name || a}`.localeCompare(b.name || b, undefined, {
        numeric: true,
      });
    });
  }, []);

  const { isTimelinePath, timelineType } = timelineData;
  const isFutureOrPresent = timelineType === FUTURE || timelineType === PRESENT;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sortOptions = isTimelinePath
    ? isFutureOrPresent
      ? ['popular', 'new_in', 'upcoming_releases']
      : ['popular', 'date']
    : [
        'popular',
        'recently_released_sneakers',
        'new_in_apparel',
        'price_low_high',
        'price_high_low',
      ];

  const getSortOptions = useCallback(() => {
    if (!sortOptions?.length) {
      return Object.values(SORT_FILTER_MAP);
    }
    return Object.entries(SORT_FILTER_MAP)
      .filter(([key]) => sortOptions.includes(key))
      .map(([, value]) => value);
  }, [sortOptions]);

  const getData = useCallback(
    (filterKey: string) => {
      if (filterKey === 'sortType') {
        return getSortOptions();
      }
      if (filterKey === 'priceList') {
        return {
          min: filters.minimumPriceCents?.amountCents,
          max: filters.maximumPriceCents?.amountCents,
        };
      }
      return getSortedFilterList(filters[filterKey], filterKey);
    },
    [filters],
  );

  const getHeaders = useCallback(() => {
    switch (openDropdown) {
      case 'colorsList':
        return ['name', 'count'];
      case 'brandsList':
        return ['name', 'location', 'count'];
      case 'productCategoriesList':
        return ['name', 'count'];
      case 'gendersList':
        return ['name', 'count'];
      case 'productConditionsList':
        return ['name', 'count'];
      default:
        return ['name'];
    }
  }, [openDropdown]);

  const getCurrentSelection = useCallback(
    (filterKey: string) => {
      if (filterKey === 'sortType') {
        const categoriesNow = ((query.categories as string) || '')?.trim() || '';

        const typesNow = ((query.types as string) || '')?.trim() || '';
        if (categoriesNow === 'Apparel' && query.sortType === 'recently_released') {
          return {
            [filterKey]: new Set(['new_in_apparel']),
          };
        }
        if (
          query.sortType === 'recently_released' &&
          typesNow === 'sneakers' &&
          categoriesNow === 'shoes'
        ) {
          return {
            [filterKey]: new Set(['recently_released_sneakers']),
          };
        }
        return {
          [filterKey]: new Set(((query.sortType || '') as string).split(',')),
        };
      }
      let selectedTypes = { [filterKey]: new Set<string | undefined>([]) };
      const queryName = QUERY_NAME_DISPLAY[filterKey as TopTitleFilter] || filterKey;

      if (filterKey === 'releaseYearsList') {
        if (query.releaseDateEnd || query.releaseDateStart) {
          // this is the timestamps for upcoming for sure
          return {
            [filterKey]: new Set([`${query.releaseDateStart}-${query.releaseDateEnd}`]),
          };
        }
        if ((query?.releaseYears as string)?.length > 0) {
          return {
            [filterKey]: getRangesFromYears((query.releaseYears as string).split(',').map(Number)),
          };
        }
      } else if (filterKey === 'priceList') {
        if (query?.priceMin || query?.priceMax || query?.prices) {
          if (query.prices) {
            const priceRanges = new Set((query?.prices as string)?.split(','));
            return {
              [filterKey]: priceRanges,
            };
          }

          return {
            [filterKey]: new Set([
              `${Number.parseInt(query.priceMin as string, 10) / 100}-${Number.parseInt(query.priceMax as string, 10) / 100}`,
            ]),
          };
        }
      } else {
        selectedTypes[filterKey] = new Set(
          !query[queryName] ? [] : ((query[queryName] || '') as string).split(','),
        );

        if (filterKey === 'productCategoriesList') {
          selectedTypes = {
            ...selectedTypes,
            productTypesList: new Set(
              !query.types ? [] : ((query.types || '') as string).split(','),
            ),
            activitiesList: new Set(
              !query.activities ? [] : ((query.activities || '') as string).split(','),
            ),
          };
        }
      }

      return selectedTypes;
    },
    [query],
  );

  const hasApparels = useCallback(() => {
    const apparel =
      filters?.productCategoriesList?.some((category: any) => category.name === 'Apparel') ||
      (query?.categories || '')?.length === 0 ||
      query?.categories?.includes('Apparel');
    return apparel;
  }, [query, filters]);

  const hasSneakers = useCallback(() => {
    const sneakers =
      filters?.productCategoriesList?.some((category: any) => category.name === 'Footwear') ||
      (query?.categories || '')?.length === 0 ||
      (query?.categories?.includes('Footwear') && (query?.types || '').length === 0) ||
      query?.types?.includes('sneakers');

    return sneakers;
  }, [query, filters]);

  const firstRow = [
    'brandsList',
    'productCategoriesList',
    'gendersList',
    'sizesList',
    'priceList',
    'productConditionsList',
  ];
  const secondRow = [
    'colorsList',
    'releaseYearsList',
    'instantShip',
    'underRetail',
    'inStock',
    'sale',
  ];

  const booleanRow = ['instantShip', 'underRetail', 'inStock', 'sale'];

  const getAllCategories = useCallback(() => {
    const names = new Set();

    function recurse(value) {
      if (Array.isArray(value)) {
        value.forEach(recurse);
      } else if (value && typeof value === 'object') {
        if (value.name) {
          names.add(value.name);
        }

        Object.values(value).forEach(recurse);
      }
    }

    recurse(filters?.productCategoriesList || []);

    return [...names];
  }, [filters]);

  return (
    <>
      <div className="tw-relative">
        {/* sort first */}
        <div className="tw-px-4 tw-pb-2 tw-border-b tw-mb-0" data-qa="mobile_sort_filter_wrapper">
          <div className="tw-flex tw-items-center tw-gap-0 " id="row-filter-1">
            {/* sort this thing first */}
            <DropdownTrigger
              isSelected={
                ((query.sortType || '').length === 0 && openDropdown === 'sortType') ||
                (query.sortType || '').length > 0
              }
              title="Sort"
              onClick={() => {
                setOpenDropdown((prev: any) => (prev === 'sortType' ? undefined : 'sortType'));
                sendTrackingEvent('FILTER_EXPAND_TAP', {
                  service_name: 'consumer_search',
                  event_context: eventTrackingEventContext,
                  filter_name: 'sort',
                  ...(query?.query && { search_string: query.query.toString() }),
                });
              }}
            />

            {firstRow.map((topTitle: string) => {
              if (topTitle === 'priceList') {
                if (!filters.maximumPriceCents && !filters.minimumPriceCents) {
                  return null;
                }
              } else if (!filters[topTitle]) {
                return null;
              }

              return (
                <DropdownBodyDrops
                  key={topTitle}
                  topTitle={topTitle}
                  query={query}
                  openDropdown={openDropdown}
                  setOpenDropdown={(e) => {
                    if (e === 'sizesList' && (!chartSize || chartSize?.length === 0)) {
                      return;
                    }
                    setOpenDropdown(e);
                  }}
                />
              );
            })}
          </div>
          <div className="tw-flex tw-items-center tw-gap-0 tw-mt-2" id="row-filter-2">
            {secondRow.map((topTitle: string) => {
              if (!filters[topTitle]) {
                return null;
              }

              return (
                <DropdownBodyDrops
                  key={topTitle}
                  topTitle={topTitle}
                  query={query}
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  rounded
                />
              );
            })}
            {booleanRow.map((booleanTitle: string) => {
              switch (booleanTitle) {
                case 'inStock':
                  if (!filters.maximumPriceCents) return null;
                  break;
                case 'sale':
                  if (
                    !filters.underRetail ||
                    (Array.isArray(query.categories)
                      ? query.categories.some((cat) => cat.toLowerCase().includes('footwear'))
                      : query.categories?.toLowerCase()?.includes('footwear'))
                  )
                    return null;
                  break;
                case 'underRetail':
                  if (
                    !filters.underRetail ||
                    (Array.isArray(query.categories)
                      ? query.categories.some((cat) => cat.toLowerCase().includes('apparel'))
                      : query.categories?.toLowerCase()?.includes('apparel'))
                  )
                    return null;
                  break;
                default:
                  if (!filters[booleanTitle]) return null;
              }

              if (!hasApparels() && booleanTitle === 'sale') return null;
              return (
                <DropdownTrigger
                  key={booleanTitle}
                  isSelected={(query?.[booleanTitle] || '').length > 0}
                  title={NAME_DISPLAY[booleanTitle]}
                  onClick={() => {
                    const newQuery: any = { ...query };

                    if (newQuery[booleanTitle] === 'true') {
                      delete newQuery[booleanTitle];
                    } else {
                      newQuery[booleanTitle] = 'true';
                    }

                    const eventNameMap = {
                      instantShip: 'instant_ship_lowest_price_cents',
                      underRetail: 'discount_tag',
                      inStock: 'is_in_stock',
                      sale: 'discount_tag',
                    };
                    sendTrackingEvent('FILTER_APPLY_TAP', {
                      service_name: 'consumer_search',
                      event_context: eventTrackingEventContext,
                      filter_name: eventNameMap[booleanTitle],
                      filter_selection:
                        booleanTitle === 'sale'
                          ? 'sale'
                          : booleanTitle === 'underRetail'
                            ? 'under_retail'
                            : newQuery[booleanTitle] || 'false',
                      ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                    });

                    void router.push(
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                      `${window.location.pathname}?${new URLSearchParams({ ...newQuery, pageNumber: '1' }).toString()}`,
                    );
                  }}
                  rounded
                />
              );
            })}
          </div>
        </div>
        {openDropdown && (
          <div
            className="tw-fixed tw-z-10 tw-w-full tw-p-4 tw-pt-0 dropdown-body"
            style={{
              top: secondRow.includes(openDropdown) ? 130 : 130,
            }}
          >
            {openDropdown === 'sizesList' ? (
              <div
                className="tw-border tw-border-solid tw-relative tw-flex tw-flex-col dropdown-body"
                data-qa="size_modal"
                style={{
                  backgroundColor: currentTheme?.backgroundColor || '#fff',
                  maxHeight: '340px',
                }}
              >
                <div
                  className="tw-flex-1 tw-overflow-y-auto"
                  style={{
                    paddingTop: 16,
                    paddingBottom: 16,
                  }}
                >
                  {!chartSize || chartSize?.length === 0 ? (
                    <div />
                  ) : (
                    <div className="tw-p-4">
                      <SearchFilterSizeMobile
                        key={openDropdown}
                        isTransformed
                        facet={chartSize || {}}
                        selectedFilterValues={selectedFilterValues}
                        setSelectedFilterValues={setSelectedFilterValues}
                      />
                    </div>
                  )}
                </div>
                <MobileFilter.Footer>
                  <div>
                    <MobileFilter.Select
                      data-qa="size_modal_select"
                      onClick={() => {
                        let newQuery: any = { ...query };
                        if (selectedFilterValues.length === 0) {
                          delete newQuery.sizes;
                        } else {
                          newQuery = {
                            ...newQuery,
                            sizes: [...selectedFilterValues].join(','),
                          };
                        }
                        setOpenDropdown(undefined);
                        sendTrackingEvent('FILTER_APPLY_TAP', {
                          service_name: 'consumer_search',
                          event_context: eventTrackingEventContext,
                          filter_name: 'size',
                          filter_selection: [...selectedFilterValues].join(','),
                          ...(getAppliedFilters(newQuery) && {
                            applied_filters: getAppliedFilters(newQuery),
                          }),
                          ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                        });
                        void router.push(
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                          `${window.location.pathname}?${new URLSearchParams({ ...newQuery, pageNumber: '1' }).toString()}`,
                        );
                      }}
                    >
                      {t(`${TP}.select`, 'Select')}
                    </MobileFilter.Select>
                  </div>
                </MobileFilter.Footer>
              </div>
            ) : openDropdown === 'priceList' ? (
              <div
                className="tw-border tw-border-solid tw-relative dropdown-body"
                data-qa="price_modal"
                style={{
                  backgroundColor: currentTheme?.backgroundColor || '#fff',
                }}
              >
                <div
                  className="tw-p-0 tw-pb-4 tw-max-h-[340px] tw-overflow-hidden"
                  style={{
                    paddingTop: 16,
                  }}
                >
                  <div className="tw-p-4 tw-max-h-[340px] tw-overflow-y-auto">
                    <SearchFilterPriceBucketMobile
                      facet={PRICE_BUCKETS_FACET as any}
                      selectedFilterValues={selectedPriceRange}
                      setSelectedFilterValues={setSelectedPriceRange}
                    />
                  </div>
                </div>
                <MobileFilter.Footer>
                  <div>
                    <MobileFilter.Select
                      data-qa="size_modal_select"
                      onClick={() => {
                        const priceRange = Array.isArray(selectedPriceRange)
                          ? selectedPriceRange
                          : [];
                        let newQuery: any = { ...query };

                        if (!selectedPriceRange || !priceRange || priceRange.length === 0) {
                          delete newQuery.prices;

                          if (Array.isArray(selectedPriceRange)) {
                            delete newQuery.priceMin;
                            delete newQuery.priceMax;
                          } else {
                            if (!selectedPriceRange?.split('-')[0]) {
                              delete newQuery.priceMin;
                            } else {
                              newQuery.priceMin = selectedPriceRange?.split('-')[0];
                            }

                            if (!selectedPriceRange?.split('-')[1]) {
                              delete newQuery.priceMax;
                            } else {
                              newQuery.priceMax = selectedPriceRange?.split('-')[1];
                            }
                          }

                          sendTrackingEvent('FILTER_APPLY_TAP', {
                            service_name: 'consumer_search',
                            event_context: eventTrackingEventContext,
                            filter_name: 'lowest_price_cents_bucket',
                            filter_selection: `${newQuery.priceMin}-${newQuery.priceMax}`,
                            ...(getAppliedFilters(newQuery) && {
                              applied_filters: getAppliedFilters(newQuery),
                            }),
                            ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                          });
                        } else {
                          newQuery = {
                            ...newQuery,
                            prices: selectedPriceRange.join(','),
                          };
                          sendTrackingEvent('FILTER_APPLY_TAP', {
                            service_name: 'consumer_search',
                            event_context: eventTrackingEventContext,
                            filter_name: 'lowest_price_cents_bucket',
                            filter_selection: selectedPriceRange.join(','),
                            ...(getAppliedFilters(newQuery) && {
                              applied_filters: getAppliedFilters(newQuery),
                            }),
                            ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                          });
                        }
                        setOpenDropdown(undefined);
                        void router.push(
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                          `${window.location.pathname}?${new URLSearchParams({ ...newQuery, pageNumber: '1' }).toString()}`,
                        );
                      }}
                    >
                      {t(`${TP}.select`, 'Select')}
                    </MobileFilter.Select>
                  </div>
                </MobileFilter.Footer>
              </div>
            ) : (
              <DropdownBody
                headers={getHeaders()}
                hideHeader={['sortType', 'gendersList', 'priceList'].includes(openDropdown)}
                filterKey={openDropdown}
                data={getData(openDropdown)}
                selectedItems={getCurrentSelection(openDropdown) as any}
                closeDropdown={() => {
                  setOpenDropdown(undefined);
                }}
                hasSneakers={hasSneakers()}
                hasApparels={hasApparels()}
                isMultiSelect={openDropdown !== 'sortType'}
                getItemFilterValue={(item: any) => {
                  return openDropdown === 'sortType' ? item.key : item.originName || item.name;
                }}
                onSubmitSelection={(selection: any) => {
                  const queryName =
                    QUERY_NAME_DISPLAY[openDropdown as TopTitleFilter] || openDropdown;

                  let newQuery: any = {};

                  if (openDropdown === 'sortType') {
                    const sortKey = [...selection.sortType][0];

                    switch (sortKey) {
                      case 'new_in_apparel':
                        hasTouchedFilters.current = false;
                        newQuery = {
                          categories: 'Apparel',
                          sortType: sortKey,
                          pageNumber: 1,
                          query: query.query || '',
                        };
                        break;
                      case 'recently_released_sneakers':
                        hasTouchedFilters.current = false;
                        newQuery = {
                          productTypes: 'sneakers',
                          categories: 'footwear',
                          sortType: sortKey,
                          releaseDateStart: getTimeStamps({
                            year: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).getFullYear(),
                            month: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).getMonth(),
                            day: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).getDate(),
                          }),
                          releaseDateEnd: getTimeStamps({
                            year: new Date(Date.now()).getFullYear(),
                            month: new Date(Date.now()).getMonth(),
                            day: new Date(Date.now()).getDate(),
                          }),
                          pageNumber: 1,
                          query: query.query || '',
                        };
                        break;
                      case 'upcoming_releases':
                        newQuery = {
                          ...query,
                          sortType: sortKey,
                          releaseDateStart:
                            timelineType === PRESENT
                              ? getTimeStamps({
                                  year: new Date(Date.now()).getFullYear(),
                                  month: new Date(Date.now()).getMonth(),
                                  day: new Date(Date.now()).getDate() + 1,
                                })
                              : getTimeStamps({
                                  year: new Date(Date.now()).getFullYear(),
                                  month: new Date(Date.now()).getMonth() + 1,
                                  day: 1,
                                }),
                          releaseDateEnd:
                            timelineType === PRESENT
                              ? getTimeStamps({
                                  year: new Date(Date.now()).getFullYear(),
                                  month: new Date(Date.now()).getMonth(),
                                  day: new Date(
                                    new Date(Date.now()).getFullYear(),
                                    new Date(Date.now()).getMonth() + 1,
                                    0,
                                  ).getDate(),
                                })
                              : getTimeStamps({
                                  year: new Date(Date.now()).getFullYear() + 1,
                                  month: new Date(Date.now()).getMonth() + 1,
                                  day: new Date(
                                    new Date(Date.now()).getFullYear(),
                                    new Date(Date.now()).getMonth() + 1,
                                    0,
                                  ).getDate(),
                                }),
                          pageNumber: 1,
                        };
                        break;
                      default:
                        if (hasTouchedFilters.current === false) {
                          delete query.categories;
                          delete query.types;
                          delete query.releaseDateStart;
                          delete query.releaseDateEnd;
                        }
                        newQuery = {
                          ...query,
                          sortType: sortKey,
                        };
                    }

                    sendTrackingEvent('FILTER_SORT_TAP', {
                      service_name: 'consumer_search',
                      event_context: eventTrackingEventContext,
                      sort_selection: sortKey,
                    });
                  } else {
                    hasTouchedFilters.current = true;
                    if (queryName === 'releaseYears') {
                      newQuery = {
                        ...query,
                        pageNumber: 1,
                        ...selection[openDropdown],
                      };
                      if (selection[openDropdown].releaseYears) {
                        delete newQuery.releaseDateStart;
                        delete newQuery.releaseDateEnd;
                      } else {
                        delete newQuery.releaseYears;
                      }
                      sendTrackingEvent('FILTER_APPLY_TAP', {
                        service_name: 'consumer_search',
                        event_context: eventTrackingEventContext,
                        filter_name: 'release_date_year_bucket',
                        filter_selection: selection[openDropdown].releaseYears,
                        ...(getAppliedFilters(newQuery) && {
                          applied_filters: getAppliedFilters(newQuery),
                        }),
                        ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                      });
                    } else if (queryName === 'priceList') {
                      newQuery = {
                        ...query,
                        prices: `${selection[openDropdown].priceMin * 100}-${selection[openDropdown].priceMax * 100}`,
                      };
                      sendTrackingEvent('FILTER_APPLY_TAP', {
                        service_name: 'consumer_search',
                        event_context: eventTrackingEventContext,
                        filter_name: 'lowest_price_cents_bucket',
                        filter_selection: `${selection[openDropdown].priceMin * 100}-${selection[openDropdown].priceMax * 100}`,
                        ...(getAppliedFilters(newQuery) && {
                          applied_filters: getAppliedFilters(newQuery),
                        }),
                        ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                      });
                    } else {
                      const newSelection = [...selection[openDropdown]].join(',');
                      newQuery = {
                        ...query,
                        ...(newSelection && { [queryName]: newSelection }),
                      };
                      if (!newSelection) delete newQuery[queryName];

                      sendTrackingEvent('FILTER_APPLY_TAP', {
                        service_name: 'consumer_search',
                        event_context: eventTrackingEventContext,
                        filter_name: 'web_groups',
                        filter_selection: [...selection[openDropdown]].join(','),
                        ...(getAppliedFilters(newQuery) && {
                          applied_filters: getAppliedFilters(newQuery),
                        }),
                        ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                      });

                      if (openDropdown === 'productCategoriesList') {
                        const productTypeSelection = [...selection.productTypesList].join(',');
                        const activitySelection = [...selection.activitiesList].join(',');
                        newQuery = {
                          ...newQuery,
                          ...(productTypeSelection && { types: productTypeSelection }),
                          ...(activitySelection && { activities: activitySelection }),
                        };
                        if (!productTypeSelection) delete newQuery.types;
                        if (!activitySelection) delete newQuery.activities;

                        sendTrackingEvent('FILTER_APPLY_TAP', {
                          service_name: 'consumer_search',
                          event_context: eventTrackingEventContext,
                          filter_name: 'web_groups',
                          filter_selection: [...selection.activitiesList].join(','),
                          ...(getAppliedFilters(newQuery) && {
                            applied_filters: getAppliedFilters(newQuery),
                          }),
                          ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                        });

                        sendTrackingEvent('FILTER_APPLY_TAP', {
                          service_name: 'consumer_search',
                          event_context: eventTrackingEventContext,
                          filter_name: 'web_groups',
                          filter_selection: [...selection.productTypesList].join(','),
                          ...(getAppliedFilters(newQuery) && {
                            applied_filters: getAppliedFilters(newQuery),
                          }),
                          ...(newQuery?.query && { search_string: newQuery.query.toString() }),
                        });
                      }
                    }
                  }

                  // todo: for price check if you want the user to select the price on error
                  setOpenDropdown(undefined);
                  if (newQuery.query && newQuery.query.trim().length === 0) {
                    delete newQuery.query;
                  }
                  void router.push(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    `${window.location.pathname}?${new URLSearchParams({ ...newQuery, pageNumber: '1' }).toString()}`,
                  );
                }}
              />
            )}
          </div>
        )}
      </div>
      {forCrawler && (
        <MobileFilter.CrawlerFilters>
          <SearchFilterBrandCrawler
            facet={
              {
                options: filters.brandsList?.map((v) => ({
                  value: normalizeValue(v.name)?.toLowerCase(),
                  display_name: v.name,
                })),
                name: 'brand',
              } as any
            }
            name="brand"
          />
        </MobileFilter.CrawlerFilters>
      )}

      {forCrawler && (
        <MobileFilter.CrawlerFilters>
          <SearchFilterCategoryMobileCrawler
            facet={{
              data: {},
              display_name: 'Category',
              hidden: false,
              name: 'web_groups',
              options: getAllCategories().map((v) => ({
                value: normalizeValue(v === 'Footwear' ? 'Sneakers' : v)?.toLowerCase(),
                display_name: v,
              })),
            }}
            topLevelCategory="all"
          />
        </MobileFilter.CrawlerFilters>
      )}
    </>
  );
};

MobileFilter.CrawlerFilters = styled.div`
  display: none;
`;
MobileFilter.Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.navigation.borderColor};
  margin-top: auto;
`;

MobileFilter.Select = styled.a`
  align-items: center;
  justify-content: center;
  cursor: pointer;
  display: flex;
  padding: 10px;
  text-transform: uppercase;
  width: 100%;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};

  &:hover {
    text-decoration: underline;
  }
`;
